import {
  DELAY_REASONS,
  RETURN_APPROVE_REASON_CODES,
  normalizeDelayReasonCode,
} from "@/constants";
import { formatDate, getDateByTimeZone } from "@/lib/date";
import {
  getCorpUserDisplayName,
  parseEtc4,
  renderApprovalDisplay,
} from "@/lib/approvalDisplay";

import BaseModel from "./base-model";
import {
  DELIVERY_STATUS_5,
  DELIVERY_STATUS_6,
  DELIVERY_STATUS_7,
  parseDeliveryStatus,
  renderStatus,
} from "./Delivery";

const LIST_STATUS_LABEL = {
  inProgress: "배송완료 전",
  completed: "배송완료",
  sentBack: "반송완료",
  lost: "분실완료",
  canceled: "취소",
};

const RETURN_EXCLUDED_LIST_STATUS = new Set(["completed", "sentBack", "lost"]);
const RETURN_EXCLUDED_DELIVERY_STATUS = new Set([
  DELIVERY_STATUS_5,
  DELIVERY_STATUS_6,
  DELIVERY_STATUS_7,
]);

const RETURN_APPROVE_REASON_TEXTS = new Set(
  DELAY_REASONS.filter((item) =>
    RETURN_APPROVE_REASON_CODES.includes(item.code),
  ).flatMap((item) =>
    [item.text, item.shortText]
      .filter(Boolean)
      .map((value) => value.replace(/\s/g, "")),
  ),
);

const compactText = (value = "") => String(value).replace(/\s/g, "");

export default class DelayedDelivery extends BaseModel {
  renderDateTime(value) {
    if (!value) {
      return "";
    }

    return formatDate(getDateByTimeZone(value), "yyyy-MM-dd HH:mm");
  }

  renderBookId() {
    return this.bookId || this.delivery?.bookId || "";
  }

  renderOrderIdFromCorp() {
    return this.orderIdFromCorp || this.delivery?.orderIdFromCorp || "";
  }

  renderOrderIdFromCorpSub() {
    return this.orderIdFromCorpSub || this.delivery?.orderIdFromCorpSub || "";
  }

  getRawDeliveryStatus() {
    return this.deliveryStatus ?? this.delivery?.status;
  }

  renderDeliveryStatus() {
    const status = this.getRawDeliveryStatus();

    if (status == null || status === "") {
      return "";
    }

    if (LIST_STATUS_LABEL[status]) {
      return LIST_STATUS_LABEL[status];
    }

    if (typeof status === "number" || /^\d+$/.test(String(status))) {
      return renderStatus(parseDeliveryStatus(status));
    }

    const labeled = renderStatus(status);

    return labeled === "접수" && status !== "DELIVERY_STATUS_RECEIPT"
      ? status
      : labeled;
  }

  getNormalizedDelayReasonCode() {
    return normalizeDelayReasonCode(
      this.delayedReasonCode || this.delayReasonCode || "",
    );
  }

  renderDelayedReason() {
    if (this.delayedReason) {
      return this.delayedReason;
    }

    const code = this.getNormalizedDelayReasonCode();

    if (!code) {
      return "";
    }

    return DELAY_REASONS.find((item) => item.code === code)?.text || code;
  }

  renderDelayedReasonAt() {
    return this.renderDateTime(this.delayedReasonAt);
  }

  getEtc4Obj() {
    if (this._etc4Obj) {
      return this._etc4Obj;
    }

    this._etc4Obj = parseEtc4(
      this.deliveryEtc4 || this.etc4 || this.delivery?.etc4,
    );
    return this._etc4Obj;
  }

  getReturnApprovedAt() {
    return this.returnApprovedAt || this.getEtc4Obj().returnApprovedAt || "";
  }

  getReturnApprovedByCorpUserId() {
    return (
      this.returnApprovedByCorpUserId ??
      this.getEtc4Obj().returnApprovedByCorpUserId ??
      ""
    );
  }

  getReturnApproverName() {
    return (
      this.returnApproverName ||
      this.returnApprovedByName ||
      getCorpUserDisplayName(this.returnApprovedByCorpUser) ||
      getCorpUserDisplayName(this.getEtc4Obj().returnApprovedByCorpUser) ||
      ""
    );
  }

  renderReturnApprovedAt() {
    return renderApprovalDisplay({
      approvedAt: this.getReturnApprovedAt(),
      corpUserId: this.getReturnApprovedByCorpUserId(),
      displayName: this.getReturnApproverName(),
      allowAuto: true,
      formatAt: (value) => this.renderDateTime(value),
    });
  }

  renderLatestCommentWriterName() {
    return (
      this.latestCommentWriterName ||
      this.latestComment?.writerName ||
      ""
    );
  }

  renderLatestCommentCreatedAt() {
    return this.renderDateTime(
      this.latestCommentCreatedAt || this.latestComment?.createdAt,
    );
  }

  renderCommentConfirmed() {
    const value =
      this.commentConfirmed ??
      this.latestCommentConfirmed ??
      this.latestComment?.isConfirmed;

    if (value == null) {
      return "";
    }

    return value ? "확인" : "미확인";
  }

  renderReceiptLabel() {
    const parts = [
      this.renderBookId(),
      this.renderOrderIdFromCorp(),
      this.renderOrderIdFromCorpSub(),
    ].filter(Boolean);

    return parts.join("/");
  }

  isReturnApproveExcludedStatus() {
    const status = this.getRawDeliveryStatus();

    if (status == null || status === "") {
      return false;
    }

    if (RETURN_EXCLUDED_LIST_STATUS.has(status)) {
      return true;
    }

    if (typeof status === "number" || /^\d+$/.test(String(status))) {
      return RETURN_EXCLUDED_DELIVERY_STATUS.has(parseDeliveryStatus(status));
    }

    return RETURN_EXCLUDED_DELIVERY_STATUS.has(status);
  }

  isReturnApproveReason() {
    const code = this.getNormalizedDelayReasonCode();

    if (code && RETURN_APPROVE_REASON_CODES.includes(code)) {
      return true;
    }

    const text = compactText(this.delayedReason || this.renderDelayedReason());

    return Boolean(text) && RETURN_APPROVE_REASON_TEXTS.has(text);
  }

  canApproveReturn() {
    return (
      this.canReturnApprove === true &&
      !this.isReturnApproveExcludedStatus() &&
      this.isReturnApproveReason()
    );
  }

  canWaitReturn() {
    return (
      this.canReturnWait === true &&
      !this.isReturnApproveExcludedStatus() &&
      this.isReturnApproveReason()
    );
  }
}
